import { inferenceHttp } from '@/lib/api/inference-client';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';
import { AccessControlService } from '@inference.engine/api-client/web';

/**
 * Identifying scope for an agent visibility check.
 *
 * `workspaceId` is nullable because the current workspace store can be
 * uninitialized (e.g. during early-mount or unauthenticated states). The
 * underlying access-control endpoint accepts `undefined` and treats it as
 * org-scoped only — matching the legacy `getWorkspaceId() ?? undefined` path.
 */
export interface AgentVisibilityScope {
  organizationId: string;
  workspaceId: string | null | undefined;
}

/**
 * Checks if a specific agent is enabled/accessible for the given workspace
 * and organization scope.
 *
 * `entities/` code MUST NOT value-import from
 * sibling entities. The caller is therefore responsible for resolving
 * `workspaceId` (e.g. via `useCurrentWorkspaceId` / `getWorkspaceId` from
 * `@/entities/workspace`) and `organizationId` (e.g. via
 * `getCurrentOrganizationId` from `@/entities/user`) and passing them in.
 *
 * @param agentId - The ID of the agent to check
 * @param scope - Workspace + organization context for the access check
 * @returns true if agent is accessible (200 response), false if not (403/404)
 */
export const checkAgentEnabled = async (
  agentId: string,
  scope: AgentVisibilityScope,
): Promise<boolean> => {
  try {
    const response = await AccessControlService.isAgentAllowed({
      client: inferenceHttp,
      path: { agentId },
      query: {
        organizationId: scope.organizationId,
        workspaceId: scope.workspaceId ?? undefined,
      },
      headers: {
        // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
        [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true',
      },
    });
    return response.data ?? false;
  } catch {
    return false;
  }
};
